import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

// Preserve the original onboarding cadence and bubble/composer layout.
const START_DELAY = 420;
const MESSAGE_GAP = 520;
const CHARACTER_DELAY = 24;

export default function OnboardingChat({ messages, step, children }) {
  const { language } = useLanguage();
  const copy = (en, ar) => (language === 'ar' ? ar : en);
  const [characters, setCharacters] = useState({});
  const [reducedMotion, setReducedMotion] = useState(() =>
    Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches),
  );
  const scrollRef = useRef(null);
  const followLatest = useRef(true);
  const previousQuestion = useRef(null);
  const currentQuestion = messages[messages.length - 1];
  const activeIndex = reducedMotion
    ? -1
    : messages.findIndex(
        (message) =>
          message.role === 'assistant' &&
          (characters[message.id] || 0) < message.content.length,
      );
  const active = messages[activeIndex];
  const activeId = active?.id;
  const count = active ? characters[active.id] || 0 : 0;
  const questionReady =
    reducedMotion ||
    (characters[currentQuestion.id] || 0) >= currentQuestion.content.length;

  useEffect(() => {
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const update = (event) => setReducedMotion(event.matches);
    media?.addEventListener?.('change', update);
    return () => media?.removeEventListener?.('change', update);
  }, []);

  useEffect(() => {
    if (!activeId) return undefined;
    const timer = window.setTimeout(
      () => {
        setCharacters((previous) => ({ ...previous, [activeId]: count + 1 }));
      },
      count ? CHARACTER_DELAY : activeIndex === 0 ? START_DELAY : MESSAGE_GAP,
    );
    return () => window.clearTimeout(timer);
  }, [activeId, activeIndex, count]);

  useEffect(() => {
    if (previousQuestion.current !== currentQuestion.id) {
      previousQuestion.current = currentQuestion.id;
      followLatest.current = true;
    }
    const node = scrollRef.current;
    if (node && followLatest.current) node.scrollTop = node.scrollHeight;
  }, [characters, currentQuestion.id, messages, questionReady]);

  return (
    <section
      className="js-onboarding__agent has-progress js-onboarding__agent--grouped"
      dir={language === 'ar' ? 'rtl' : 'ltr'}
      aria-label={copy('Chat with Sigmund', 'محادثة مع سيغموند')}
    >
      <div
        className="js-onboarding__progress"
        aria-label={copy(
          `Question ${step + 1} of 3`,
          `السؤال ${step + 1} من 3`,
        )}
      >
        <div className="js-onboarding__progress-meta">
          <span>{copy('Getting to know you', 'لنتعرّف عليك')}</span>
          <span>{copy(`${step + 1} of 3`, `${step + 1} من 3`)}</span>
        </div>
        <div className="js-onboarding__progress-track" aria-hidden="true">
          <span style={{ width: `${((step + 1) / 3) * 100}%` }} />
        </div>
      </div>
      <div
        className="js-onboarding__chat"
        ref={scrollRef}
        role="log"
        aria-label={copy('Conversation', 'المحادثة')}
        aria-live="polite"
        onScroll={() => {
          const node = scrollRef.current;
          followLatest.current =
            node.scrollHeight - node.scrollTop - node.clientHeight < 48;
        }}
      >
        {messages.map((message, index) => {
          const complete =
            reducedMotion ||
            message.role === 'user' ||
            (characters[message.id] || 0) >= message.content.length;
          if (!complete && (index !== activeIndex || !count)) return null;
          const content = complete
            ? message.content
            : message.content.slice(0, count);
          return (
            <div
              key={message.id}
              className={`js-onboarding__bubble is-${message.role}`}
            >
              <span aria-hidden={!complete || undefined}>{content}</span>
              {!complete && (
                <span className="js-onboarding__caret" aria-hidden="true" />
              )}
            </div>
          );
        })}
        {active && !count && (
          <div
            className="js-onboarding__typing"
            aria-label={copy('Sigmund is typing', 'سيغموند يكتب')}
          >
            <span />
            <span />
            <span />
          </div>
        )}
      </div>
      <div className="js-onboarding__composer" hidden={!questionReady}>
        {children}
      </div>
    </section>
  );
}
